<script lang="ts">
    import { globalLoggerToken } from "@/tokens";
    import Timer from "@lib/timer";
    import { AnalyticsEventsCollectorModule } from "@modules/analytics_events_collector/AnalyticsEventsCollectorModule";
    import CN from "classnames";
    import { createEventDispatcher } from "svelte";
    import { container } from "tsyringe";

    export let src: string;
    export let fallback: string | undefined = undefined;
    export let alt: string;
    export let ratio: number | undefined = undefined;
    export let objectFit: "cover" | "contain" | undefined = undefined;
    export let objectPosition: "top" | "bottom" | "center" | undefined =
        undefined;
    export let wrapperClassName: string | undefined = undefined;
    export let imgClassName: string | undefined = undefined;
    export let backdropClassName: string | undefined = undefined;
    export let blur = false;
    export let themeColor = false;
    export let loading: "eager" | "lazy" = "eager";
    export let ariaHidden = false;
    let loaded = false;
    let useFallback = false;
    let paddingTop = ratio ? (1 / ratio) * 100 : undefined;
    const dispatch = createEventDispatcher();
    const loadImageTimer = new Timer();

    const analyticsEventsCollectorModule = container.resolve(
        AnalyticsEventsCollectorModule
    );
    const eventsCollector =
        analyticsEventsCollectorModule.exports.eventsCollector;
    const analyticsEventsFactory =
        analyticsEventsCollectorModule.exports.eventsFactory;
    const handleLoad = () => {
        eventsCollector.pushEvent(
            analyticsEventsFactory.createImageLoaded(
                src,
                loadImageTimer.ready()
            )
        );
        dispatch("imageLoaded");
        loaded = true;
    };
    const handleError = () => {
        if (fallback) {
            useFallback = true;
        }
        const logger = container.resolve(globalLoggerToken);
        logger.warn("Error on load image");
        eventsCollector.pushEvent(
            analyticsEventsFactory.createLoadImageError(src)
        );
        dispatch("imageLoadingError");
    };
    $: {
        src;
        loaded = false;
    }
</script>

<div
    style={[
        objectFit ? `--gw-image-object-fit: ${objectFit};` : undefined,
        paddingTop ? `--gw-image-pt: ${paddingTop}%;` : undefined,
        objectPosition
            ? `--gw-image-object-position: ${objectPosition};`
            : undefined,
    ]
        .filter(Boolean)
        .join("")}
    class={CN("gw-image", wrapperClassName, {
        "gw-image--loaded": loaded,
        "gw-image--not-loaded": !loaded,
        "gw-image--force-ratio": ratio && objectFit,
    })}
>
    <div class={CN("gw-image__backdrop", backdropClassName)}>
        {#if blur || themeColor}
            <img
                src={useFallback ? fallback : src}
                {alt}
                class={CN({
                    "gw-image__backdrop-blur": blur,
                    "gw-image__backdrop-theme-color": themeColor,
                    "gw-image__backdrop-blur-loaded": loaded,
                })}
                aria-hidden={ariaHidden}
                {loading}
            />
        {/if}
        <slot name="backdrop" />
    </div>
    <img
        on:error={handleError}
        on:load={handleLoad}
        class={CN("gw-image__img", imgClassName)}
        {alt}
        src={useFallback ? fallback : src}
        aria-hidden={ariaHidden}
        {loading}
    />
</div>

<style>
    .gw-image {
        position: relative;
        height: 100%;
        overflow: hidden;
    }
    .gw-image.gw-image--force-ratio,
    .gw-image.gw-image--not-loaded {
        height: 0;
        padding-top: var(--gw-image-pt);
    }
    .gw-image__img {
        width: 100%;
        height: 100%;
        object-fit: var(--gw-image-object-fit);
        object-position: var(--gw-image-object-position);
        transition: all var(--gw-default-transition);
    }
    .gw-image.gw-image--force-ratio .gw-image__img,
    .gw-image.gw-image--not-loaded .gw-image__img {
        display: block;
        position: absolute;
        top: 0;
        left: 0;
        opacity: 0;
    }
    .gw-image.gw-image--loaded .gw-image__img {
        display: block;
        opacity: 1;
    }
    .gw-image__backdrop {
        display: block;
        position: absolute;
        z-index: -1;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        background-color: var(--gw-image-backdrop-color, #eee);
    }
    .gw-image.gw-image--not-loaded .gw-image__backdrop {
        display: block;
    }

    .gw-image__backdrop-blur {
        width: 100%;
        height: 100%;
        filter: blur(10px);
        opacity: 0;
    }

    .gw-image__backdrop-theme-color {
        width: 100%;
        height: 100%;
        filter: blur(50px);
        transform: scale(3);
        opacity: 0;
    }

    .gw-image__backdrop-blur-loaded {
        opacity: 1;
    }
</style>
